import { useEffect, useRef, useState } from "react";
import { Button, ModalWrapper, SearchableDropdown } from "../../../../components/ui";
import { X } from "../../../../components/icons/svg/outline";
import { formatDate } from "../../../../utils/datetime";
import { getAddresses } from "../../../../services/api/dispatch";

const TITLE_ID = "custom-address-modal-title";
const CLOSE_BUTTON_CLASS =
  "inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-primary-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30";

/**
 * Select a custom address for one dispatch scope day.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.scope - The scope object containing job details and custom address.
 * @param {boolean} props.open - A boolean indicating if the modal is open.
 * @param {Function} props.onClose - Function to call when the modal is closed.
 * @param {Function} props.onSubmit - Function to call when the form is submitted.
 * @returns {JSX.Element} The rendered component.
 */
export default function EditScopeCustomAddressModal({
  scope,
  open,
  onClose,
  onSubmit,
}) {
  const formRef = useRef(null);
  const [formState, setformState] = useState(null);
  const [formValues, setFormValues] = useState(null);

  useEffect(() => {
    if (open && formValues === null) {
      getAddresses()
        .then((addresses) => {
          const activeAddresses = addresses.filter((address) => address.active);
          setFormValues({ addresses: activeAddresses });
        })
        .catch((e) => console.error(e));
    }
  }, [open, formValues]);

  useEffect(() => {
    if (open && formState === null && formValues !== null) {
      const address = formValues.addresses.find(
        (address) => address.address === scope["custom_address"]
      );
      const newState = {
        custom_address: address?.id || null,
      };

      setformState(newState);
    }
  }, [formState, formValues, open, scope]);

  useEffect(() => {
    if (!open && formState !== null) {
      setformState(null);
    }
  }, [formState, open]);

  const scopeLabel = [scope.job?.number, scope.scope?.title]
    .filter(Boolean)
    .join(" - ");
  const dateLabel = scope.date ? formatDate(scope.date, "weekday MM/DD/YY") : "";
  const contextLabel = [scopeLabel, dateLabel].filter(Boolean).join(" · ");
  const canConfirm = Boolean(formState && formValues);

  /**
   * Submit the address form so the existing FormData handler still runs.
   *
   * @returns {void}
   */
  const handleConfirm = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      closeButton={false}
      maxWidth={560}
      className="relative w-full max-h-[90vh] overflow-y-auto rounded-xl bg-white p-0 shadow-xl"
      outsideClickIgnoreSelector="[data-searchable-dropdown-listbox]"
    >
      <div role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
        <div className="bg-primary px-5 py-3.5">
          <div className="flex items-center justify-between gap-3">
            <h2
              id={TITLE_ID}
              className="min-w-0 text-lg font-semibold leading-none tracking-tight text-white"
            >
              Set Custom Address
            </h2>
            <button
              type="button"
              onClick={onClose}
              className={CLOSE_BUTTON_CLASS}
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          {contextLabel ? (
            <p className="mt-1 text-sm leading-snug text-white/80">{contextLabel}</p>
          ) : null}
        </div>
        <form ref={formRef} className="space-y-4 px-5 py-4" onSubmit={onSubmit}>
          <div>
            <span className="mb-1.5 block text-sm font-semibold text-gray-800">
              Address
            </span>
            {/* Named field so the existing FormData submit still sends custom_address. */}
            <input
              type="hidden"
              name="custom_address"
              value={formState?.custom_address ?? ""}
            />
            <SearchableDropdown
              options={(formValues?.addresses ?? []).map((address) => ({
                value: address.id,
                label: address.address,
              }))}
              value={formState?.custom_address ?? null}
              onChange={(customAddress) =>
                setformState({ custom_address: customAddress })
              }
              placeholder={formValues ? "Select an address" : "Loading addresses..."}
              ariaLabel="Custom address"
              disabled={!formValues}
              listboxClassName="z-[10060]"
            />
          </div>
          <Button
            text="Confirm"
            variant={canConfirm ? "secondary" : "disabled"}
            disabled={!canConfirm}
            className="w-full rounded-lg"
            onClick={handleConfirm}
          />
        </form>
      </div>
    </ModalWrapper>
  );
}
